import React, { useEffect, useState } from "react";
import {
    Button,
    Modal,
    ModalBody,
    Input,
    Label,
    FormGroup,
    Form,
} from "reactstrap";
import { toast } from "react-toastify";
import {
    getOverrideMasterSettings,
    submitTestEmail,
} from "model/MasterSettingModel";
import Loaders from "components/Common/Loaders";

const FROM_EMAIL_OPTIONS = [
    { value: "", label: "Select From Email" },
    { value: "hello@ancile.services", label: "hello@ancile.services" },
    { value: "recruiting@beans.ai", label: "recruiting@beans.ai" },
    { value: "recruiter@ancileservices.com", label: "recruiter@ancileservices.com" },
    { value: "schedule@funnl.team", label: "schedule@funnl.team" },
];

const initialFormState = {
    to: "",
    ccEmail: "",
    fromEmail: "",
    subject: "",
    body: "",
};

const TestEmailServerModal = ({
    testEmailServerModalOpen,
    setTestEmailServerModalOpen,
}) => {
    const [formData, setFormData] = useState(initialFormState);
    const [displayName, setDisplayName] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const closeModal = () => {
        setTestEmailServerModalOpen(false);
        setFormData(initialFormState);
    };

    const loadModalContext = async () => {
        setLoading(true);
        try {
            const response = await getOverrideMasterSettings({});
            const data = response?.data || {};
            setDisplayName(
                String(data?.name ?? data?.displayName ?? data?.companyName ?? "").trim()
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (testEmailServerModalOpen) {
            setFormData(initialFormState);
            loadModalContext();
        }
    }, [testEmailServerModalOpen]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const to = String(formData?.to ?? "").trim();
        const fromEmail = String(formData?.fromEmail ?? "").trim();
        const subject = String(formData?.subject ?? "").trim();
        const body = String(formData?.body ?? "").trim();

        if (!to) {
            toast.error("To Email is required");
            return;
        }
        if (!fromEmail) {
            toast.error("From Email is required");
            return;
        }
        if (!subject) {
            toast.error("Subject is required");
            return;
        }
        if (!body) {
            toast.error("Body is required");
            return;
        }

        setSaving(true);
        try {
            const payload = {
                to,
                ccEmail: String(formData?.ccEmail ?? "").trim(),
                fromEmail,
                subject,
                body,
            };
            const response = await submitTestEmail(payload);
            if (response?.success !== false && (response?.success || response?.isSuccess)) {
                toast.success(response?.message || "Test email sent successfully");
                closeModal();
            } else {
                toast.error(response?.message || "Unable to send test email");
            }
        } finally {
            setSaving(false);
        }
    };

    const modalTitleSuffix = displayName
        ? ` - ${displayName.toUpperCase()}`
        : "";

    return (
        <Modal
            isOpen={testEmailServerModalOpen}
            toggle={closeModal}
            backdrop="static"
            className="modal-full-height"
            centered
            zIndex={5000}
        >
            <ModalBody
                className="p-4"
                style={{ maxHeight: "95vh", overflowY: "auto" }}
            >
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0 text-uppercase">
                        {`Test Email Server${modalTitleSuffix}`}
                    </h5>
                    <Button close onClick={closeModal} />
                </div>
                <hr />

                {loading ? (
                    <Loaders />
                ) : (
                    <Form onSubmit={handleSubmit}>
                        <FormGroup>
                            <Label>
                                To Email <span className="text-danger">*</span>
                            </Label>
                            <Input
                                type="email"
                                name="to"
                                value={formData.to}
                                onChange={handleChange}
                                placeholder="user@example.com"
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>To CC Email</Label>
                            <Input
                                type="email"
                                name="ccEmail"
                                value={formData.ccEmail}
                                onChange={handleChange}
                                placeholder="user@example.com"
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>
                                From Email <span className="text-danger">*</span>
                            </Label>
                            <Input
                                type="select"
                                name="fromEmail"
                                value={formData.fromEmail}
                                onChange={handleChange}
                            >
                                {FROM_EMAIL_OPTIONS.map((option) => (
                                    <option key={option.value || "from-email-empty"} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>

                        <FormGroup>
                            <Label>
                                Subject <span className="text-danger">*</span>
                            </Label>
                            <Input
                                type="text"
                                name="subject"
                                value={formData.subject}
                                onChange={handleChange}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>
                                Body <span className="text-danger">*</span>
                            </Label>
                            <Input
                                type="textarea"
                                name="body"
                                rows={5}
                                value={formData.body}
                                onChange={handleChange}
                            />
                        </FormGroup>

                        <div className="text-end mt-3">
                            <Button color="primary" type="submit" disabled={saving}>
                                <i className="fa fa-paper-plane me-1" aria-hidden="true" />
                                {saving ? "Sending..." : "Send"}
                            </Button>
                        </div>
                    </Form>
                )}
            </ModalBody>
        </Modal>
    );
};

export default TestEmailServerModal;
